import { Typography } from "@mui/material";
import {
  ListAppliedFilterChips,
  ListFiltersDrawer,
  ListTablePaginationBar,
  type ListAppliedFilterChipItem,
} from "design-components";
import { useCallback, useEffect, useMemo, useState } from "react";
import { OperatorsTable } from "../components/operators-table.component";
import { OperatorsToolbar } from "../components/operators-toolbar.component";
import type { OperatorListRow } from "../types";

const OPERATOR_ROWS: OperatorListRow[] = [
  {
    id: "operator-row-1",
    companyName: "Con Global",
    country: "United States",
    primaryContact: "John Smart",
    email: "john.s@conglobal.com",
    phone: "(555)-555-5555",
    status: "active",
    locations: "Forest View, Baytown, Location name",
  },
  {
    id: "operator-row-2",
    companyName: "Con Global",
    country: "United States",
    primaryContact: "John Smart",
    email: "john.s@conglobal.com",
    phone: "(555)-555-5555",
    status: "active",
    locations: "Forest View, Baytown, Location name",
  },
  {
    id: "operator-row-3",
    companyName: "Con Global",
    country: "United States",
    primaryContact: "John Smart",
    email: "john.s@conglobal.com",
    phone: "(555)-555-5555",
    status: "active",
    locations: "Forest View, Baytown",
  },
  {
    id: "operator-row-4",
    companyName: "Con Global",
    country: "United States",
    primaryContact: "John Smart",
    email: "john.s@conglobal.com",
    phone: "(555)-555-5555",
    status: "active",
    locations: "Forest View, Baytown, Location name",
  },
  {
    id: "operator-row-5",
    companyName: "Con Global",
    country: "Canada",
    primaryContact: "John Smart",
    email: "john.s@conglobal.com",
    phone: "(555)-555-5555",
    status: "active",
    locations: "Forest View, Baytown, Location name, Location name, Location name",
  },
  {
    id: "operator-row-6",
    companyName: "Con Global",
    country: "United States",
    primaryContact: "John Smart",
    email: "john.s@conglobal.com",
    phone: "(555)-555-5555",
    status: "inactive",
    locations: "Forest View, Baytown, Location name",
  },
  {
    id: "operator-row-7",
    companyName: "Con Global",
    country: "United States",
    primaryContact: "John Smart",
    email: "john.s@conglobal.com",
    phone: "(555)-555-5555",
    status: "active",
    locations: "Forest View",
  },
  {
    id: "operator-row-8",
    companyName: "Con Global",
    country: "United States",
    primaryContact: "John Smart",
    email: "john.s@conglobal.com",
    phone: "(555)-555-5555",
    status: "active",
    locations: "Forest View, Baytown, Location name",
  },
  {
    id: "operator-row-9",
    companyName: "Con Global",
    country: "United States",
    primaryContact: "John Smart",
    email: "john.s@conglobal.com",
    phone: "(555)-555-5555",
    status: "active",
    locations: "Forest View, Baytown, Location name",
  },
  {
    id: "operator-row-10",
    companyName: "Con Global",
    country: "United States",
    primaryContact: "John Smart",
    email: "john.s@conglobal.com",
    phone: "(555)-555-5555",
    status: "active",
    locations: "Forest View",
  },
  {
    id: "operator-row-11",
    companyName: "Con Global",
    country: "United States",
    primaryContact: "John Smart",
    email: "john.s@conglobal.com",
    phone: "(555)-555-5555",
    status: "active",
    locations: "Forest View, Baytown, Location name, Location name, Location name",
  },
  {
    id: "operator-row-12",
    companyName: "Con Global",
    country: "United States",
    primaryContact: "John Smart",
    email: "john.s@conglobal.com",
    phone: "(555)-555-5555",
    status: "active",
    locations: "Forest View, Baytown, Location name",
  },
  {
    id: "operator-row-13",
    companyName: "Con Global",
    country: "United States",
    primaryContact: "John Smart",
    email: "john.s@conglobal.com",
    phone: "(555)-555-5555",
    status: "inactive",
    locations: "Forest View, Baytown, Location name",
  },
];

const STATUS_FILTER_OPTIONS = [
  { value: "active", label: "Active" },
  { value: "inactive", label: "Inactive" },
];
const OPERATORS_LIST_PAGE_SIZE = 50;

export function OperatorsListPage() {
  const [searchValue, setSearchValue] = useState("");
  const [showOnlyActive, setShowOnlyActive] = useState(true);
  const [sortDescending, setSortDescending] = useState(true);
  const [listPage, setListPage] = useState(1);
  const [selectedOperatorIds, setSelectedOperatorIds] = useState<string[]>([]);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [draftFilters, setDraftFilters] = useState<Record<string, string[]>>({});
  const [appliedStatusFilters, setAppliedStatusFilters] = useState<string[]>([]);
  const [appliedCountryFilters, setAppliedCountryFilters] = useState<string[]>([]);

  const countryFilterOptions = useMemo(
    () =>
      Array.from(new Set(OPERATOR_ROWS.map((row) => row.country))).map((country) => ({
        value: country,
        label: country,
      })),
    [],
  );

  const listFilterSections = useMemo(
    () => [
      { id: "status", label: "Status", options: STATUS_FILTER_OPTIONS },
      { id: "country", label: "Country", options: countryFilterOptions },
    ],
    [countryFilterOptions],
  );

  const appliedChipItems: ListAppliedFilterChipItem[] = useMemo(() => {
    const items: ListAppliedFilterChipItem[] = [];
    for (const v of appliedStatusFilters) {
      const option = STATUS_FILTER_OPTIONS.find((opt) => opt.value === v);
      items.push({
        id: `status:${v}`,
        label: option?.label ?? v,
        onRemove: () => {
          setAppliedStatusFilters((prev) => prev.filter((val) => val !== v));
          setListPage(1);
        },
      });
    }
    for (const v of appliedCountryFilters) {
      items.push({
        id: `country:${v}`,
        label: v,
        onRemove: () => {
          setAppliedCountryFilters((prev) => prev.filter((val) => val !== v));
          setListPage(1);
        },
      });
    }
    return items;
  }, [appliedCountryFilters, appliedStatusFilters]);

  const handleClearAllFilterChips = useCallback(() => {
    setAppliedStatusFilters([]);
    setAppliedCountryFilters([]);
    setListPage(1);
  }, []);

  const filteredRows = useMemo(() => {
    const normalizedSearch = searchValue.trim().toLowerCase();

    const rows = OPERATOR_ROWS.filter((row) => {
      if (showOnlyActive && row.status !== "active") return false;
      if (appliedStatusFilters.length > 0 && !appliedStatusFilters.includes(row.status)) {
        return false;
      }
      if (appliedCountryFilters.length > 0 && !appliedCountryFilters.includes(row.country)) {
        return false;
      }
      if (normalizedSearch.length === 0) return true;

      const haystack = [
        row.companyName,
        row.country,
        row.primaryContact,
        row.email,
        row.phone,
        row.locations,
      ]
        .join(" ")
        .toLowerCase();

      return haystack.includes(normalizedSearch);
    });

    const sortedRows = [...rows];
    sortedRows.sort((a, b) => {
      const result = a.companyName.localeCompare(b.companyName, undefined, {
        sensitivity: "base",
      });
      return sortDescending ? result * -1 : result;
    });

    return sortedRows;
  }, [
    appliedCountryFilters,
    appliedStatusFilters,
    searchValue,
    showOnlyActive,
    sortDescending,
  ]);

  const handleCompanySortToggle = useCallback(() => {
    setSortDescending((current) => !current);
  }, []);

  const handleSearchTrigger = useCallback((nextValue: string) => {
    setSearchValue(nextValue);
    setListPage(1);
  }, []);

  const handleShowOnlyActiveChange = useCallback((value: boolean) => {
    setShowOnlyActive(value);
    setListPage(1);
  }, []);

  const handleOpenFilterDrawer = useCallback(() => {
    setDraftFilters({
      status: [...appliedStatusFilters],
      country: [...appliedCountryFilters],
    });
    setDrawerOpen(true);
  }, [appliedCountryFilters, appliedStatusFilters]);

  const handleApplyFilters = useCallback((nextFilters: Record<string, string[]>) => {
    setAppliedStatusFilters(nextFilters.status ?? []);
    setAppliedCountryFilters(nextFilters.country ?? []);
    setListPage(1);
    setDrawerOpen(false);
  }, []);

  useEffect(() => {
    const pageCount = Math.max(1, Math.ceil(filteredRows.length / OPERATORS_LIST_PAGE_SIZE));
    if (listPage > pageCount) {
      setListPage(pageCount);
    }
  }, [filteredRows.length, listPage]);

  const pagedRows = useMemo(() => {
    const fromIndex = (listPage - 1) * OPERATORS_LIST_PAGE_SIZE;
    return filteredRows.slice(fromIndex, fromIndex + OPERATORS_LIST_PAGE_SIZE);
  }, [filteredRows, listPage]);

  return (
    <div className="operators-page">
      <Typography variant="h5" className="operators-page__title" component="h1">
        Operators
      </Typography>
      <div className="users-page__main">
        <OperatorsToolbar
          searchValue={searchValue}
          showOnlyActive={showOnlyActive}
          onSearchTrigger={handleSearchTrigger}
          onShowOnlyActiveChange={handleShowOnlyActiveChange}
          onFilterClick={handleOpenFilterDrawer}
        />
        <ListAppliedFilterChips
          items={appliedChipItems}
          onClearAll={handleClearAllFilterChips}
          totalResults={filteredRows.length}
        />
        <div className="users-page__table-scroll">
          <OperatorsTable
            rows={pagedRows}
            selectedOperatorIds={selectedOperatorIds}
            sortDescending={sortDescending}
            onSelectedOperatorIdsChange={setSelectedOperatorIds}
            onCompanyNameHeaderClick={handleCompanySortToggle}
            getCompanyNameLink={(row) => ({
              to: `/operators/${row.id}`,
            })}
            paginationFooter={
              <ListTablePaginationBar
                totalCount={filteredRows.length}
                page={listPage}
                pageSize={OPERATORS_LIST_PAGE_SIZE}
                onPageChange={setListPage}
              />
            }
          />
        </div>
      </div>
      <ListFiltersDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        title="Filters"
        subtitle="Select filters for operators."
        sections={listFilterSections}
        value={draftFilters}
        onChange={setDraftFilters}
        onApply={handleApplyFilters}
      />
    </div>
  );
}
